import React, { useState } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import AlertIconType from '../../constants/alertIconType'
import SuccessAlert from '../../icons/SuccessAlert'
import WarnAlert from '../../icons/WarnAlert'
import i18n from '../../locales/i18n'
import { ClassesType } from '../../types'
import { omitTranslationProps } from '../../utils'
import ButtonWithLoader, {
  ButtonWithLoaderProps,
} from '../buttons/ButtonWithLoader'
import PuiCheckbox from '../inputs/PuiCheckbox'
import Text from '../Text'
import PuiDialog, { PuiDialogProps } from './PuiDialog'

const useStyles = makeStyles(
  theme => ({
    paper: {
      width: 'min-content',
      minWidth: 385,
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 32px)',
      },
    },
    container: {
      padding: theme.spacing(3),
    },
    containerTextOnly: {
      padding: theme.spacing(6, 3, 3),
    },
    icon: {
      width: 32,
      height: 32,
      marginBottom: theme.spacing(2),
    },
    warnIcon: {
      color: theme.colors.alertWarning,
    },
    successIcon: {
      color: theme.colors.alertSuccess,
    },
    message: {
      whiteSpace: 'pre-wrap',
    },
    button: {
      margin: theme.spacing(0, 1),
      minWidth: 150,
      whiteSpace: 'nowrap',
    },
  }),
  { name: 'PuiAlert' },
)

const IconMap = {
  [AlertIconType.SUCCESS]: SuccessAlert,
  [AlertIconType.WARN]: WarnAlert,
}

export interface PuiAlertProps extends Omit<PuiDialogProps, 'content'> {
  readonly CancelButtonProps?: Partial<ButtonWithLoaderProps>
  readonly OkButtonProps?: Partial<ButtonWithLoaderProps>
  readonly additionalButtons?: React.ReactElement<any, any>[]
  readonly cancelButtonText?: React.ReactNode
  readonly classes?: ClassesType<typeof useStyles>
  readonly content?: React.ReactNode
  readonly iconType?: AlertIconType
  readonly message?: React.ReactNode
  readonly okButtonText?: React.ReactNode
  readonly onAdditionalButtonClick?: (
    preventShowAgain: boolean,
    key: React.Key | null,
  ) => void
  readonly onCancel?: () => void
  readonly onOk?: (preventShowAgain: boolean) => void
  readonly preventShowAgainCheckBox?: boolean
}

const PuiAlert = ({
  preventShowAgainCheckBox,
  classes: classesProp,
  open,
  iconType,
  message,
  content,
  onOk,
  onAdditionalButtonClick,
  okButtonText,
  onCancel,
  cancelButtonText,
  CancelButtonProps: CancelButtonPropsProp,
  OkButtonProps: OkButtonPropsProp,
  additionalButtons,
  ...rest
}: PuiAlertProps) => {
  const CancelButtonProps = CancelButtonPropsProp || {}
  const OkButtonProps = OkButtonPropsProp || {}
  const additionalButtonsProp = additionalButtons || []

  const classes = useStyles({ classes: classesProp })
  const puiAlertOkButtonText =
    okButtonText || i18n.t<string>('Common:OK').toUpperCase()
  const puiAlertCancelButtonText =
    cancelButtonText || i18n.t<string>('Common:CANCEL_ACTION')

  const [preventShowAgain, setPreventShowAgain] = useState(false)

  const Icon = iconType ? IconMap[iconType] : undefined

  const AdditionalButtons = additionalButtonsProp.map(child =>
    React.cloneElement(child, {
      className: classes.button,
      onClick: () => {
        if (onAdditionalButtonClick) {
          onAdditionalButtonClick(
            preventShowAgainCheckBox ? preventShowAgain : false,
            child.key,
          )
        }
      },
    }),
  )

  return (
    <PuiDialog
      aria-labelledby="pui-alert"
      classes={{
        paper: classes.paper,
      }}
      open={open}
      {...omitTranslationProps(rest)}
    >
      <Grid
        container
        alignItems="center"
        className={Icon ? classes.container : classes.containerTextOnly}
        direction="column"
      >
        {Icon && (
          <Icon
            className={classNames(
              classes.icon,
              iconType === AlertIconType.SUCCESS && classes.successIcon,
              iconType === AlertIconType.WARN && classes.warnIcon,
            )}
          />
        )}
        {message && (
          <Text
            align="center"
            className={classes.message}
            mb={2}
            variant="body"
          >
            {message}
          </Text>
        )}
        {content}
        {preventShowAgainCheckBox && (
          <PuiCheckbox
            checked={preventShowAgain}
            label={i18n.t<string>('Dialogs:PUI_ALERT.CHECKBOX_LABEL')}
            onChange={() => setPreventShowAgain(!preventShowAgain)}
          />
        )}
        <Grid container justifyContent="center" mt={1} wrap="nowrap">
          {onCancel && (
            <ButtonWithLoader
              className={classes.button}
              color="secondary"
              onClick={onCancel}
              {...CancelButtonProps}
            >
              {puiAlertCancelButtonText}
            </ButtonWithLoader>
          )}
          {AdditionalButtons}
          {onOk && (
            <ButtonWithLoader
              className={classes.button}
              onClick={() =>
                onOk(preventShowAgainCheckBox ? preventShowAgain : false)
              }
              {...OkButtonProps}
            >
              {puiAlertOkButtonText}
            </ButtonWithLoader>
          )}
        </Grid>
      </Grid>
    </PuiDialog>
  )
}

export default PuiAlert
